/**
 * Integration Configuration Modal
 * Multi-step wizard for creating/editing integrations
 * Integrates field mapping and transformation configuration
 */

import React, { useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  Database,
  Settings,
  GitBranch,
  Shield,
  Calendar,
  AlertTriangle,
  HelpCircle,
  Layers
} from 'lucide-react';
import {
  IntegrationConfig,
  IntegrationType,
  createIntegration,
  updateIntegration,
  testConnection
} from '@/services/api/integrations';
import {
  FieldMappingStep,
  TransformationStep,
  ValidationStep,
  ScheduleStep,
  ErrorHandlingStep,
  ReviewStep,
  DataSyncTypesStep
} from './IntegrationConfigSteps';

interface Props {
  integration?: IntegrationConfig | null;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'basic' | 'connection' | 'data_types' | 'mapping' | 'transformation' | 'validation' | 'schedule' | 'error_handling' | 'review';

const BASE_STEPS: { id: Step; label: string; icon: React.ReactNode }[] = [
  { id: 'basic', label: 'Basic Info', icon: <Database className="h-4 w-4" /> },
  { id: 'connection', label: 'Connection', icon: <Settings className="h-4 w-4" /> },
  { id: 'mapping', label: 'Field Mapping', icon: <GitBranch className="h-4 w-4" /> },
  { id: 'transformation', label: 'Transformations', icon: <GitBranch className="h-4 w-4" /> },
  { id: 'validation', label: 'Validation', icon: <Shield className="h-4 w-4" /> },
  { id: 'schedule', label: 'Schedule', icon: <Calendar className="h-4 w-4" /> },
  { id: 'error_handling', label: 'Error Handling', icon: <AlertTriangle className="h-4 w-4" /> },
  { id: 'review', label: 'Review', icon: <Check className="h-4 w-4" /> },
];

// Data Types step for HRIS/Benefit Systems
const DATA_TYPES_STEP = { id: 'data_types' as Step, label: 'Data Types', icon: <Layers className="h-4 w-4" /> };

// Function to get steps based on integration type
const getStepsForType = (integrationType: string | undefined): typeof BASE_STEPS => {
  if (integrationType === 'benefit_systems') {
    // Insert Data Types step after Connection
    return [
      BASE_STEPS[0], // basic
      BASE_STEPS[1], // connection
      DATA_TYPES_STEP, // data_types (new)
      ...BASE_STEPS.slice(2) // mapping, transformation, validation, schedule, error_handling, review
    ];
  }
  return BASE_STEPS;
};

export const IntegrationConfigModal: React.FC<Props> = ({ integration, onClose, onSuccess }) => {
  const { t } = useAppTranslation();
  const isEditing = !!integration;
  const [currentStep, setCurrentStep] = useState<Step>('basic');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<IntegrationConfig>>({
    name: integration?.name || '',
    description: integration?.description || '',
    integration_type: integration?.integration_type || 'health_plan',
    adapter_type: integration?.adapter_type || '',
    is_active: integration?.is_active ?? true,
    connection_config: integration?.connection_config || {},
    field_mappings: integration?.field_mappings || {},
    transformation_rules: integration?.transformation_rules || {},
    validation_rules: integration?.validation_rules || {
      required_fields: [],
      duplicate_check: false,
    },
    schedule_config: integration?.schedule_config || {
      frequency: 'daily',
      time: '02:00',
      timezone: 'America/Los_Angeles',
      enabled: false,
    },
    error_handling_config: integration?.error_handling_config || {
      on_error: 'skip_row',
      max_retries: 3,
      retry_delay_seconds: 300,
    },
    agent_config: integration?.agent_config || {
      max_concurrent_tasks: 5,
      batch_size: 500,
      timeout_seconds: 1800,
    },
    sync_options: integration?.sync_options || [],
  });

  // Get steps dynamically based on integration type
  const STEPS = getStepsForType(formData.integration_type);
  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(STEPS[currentStepIndex + 1].id);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(STEPS[currentStepIndex - 1].id);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      if (isEditing && integration?.id) {
        await updateIntegration(integration.id, formData);
      } else {
        await createIntegration(formData as IntegrationConfig);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!integration?.id) {
      setTestResult({ success: false, message: 'Save integration first to test connection' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const result = await testConnection(integration.id);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const updateFormData = (updates: Partial<IntegrationConfig>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Integration' : 'New Integration'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {STEPS[currentStepIndex].label}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between overflow-x-auto">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded-lg transition-colors flex-shrink-0 ${
                    currentStep === step.id
                      ? 'bg-teal-100 text-teal-700'
                      : index < currentStepIndex
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {index < currentStepIndex ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <span className="h-3 w-3">{step.icon}</span>
                  )}
                  <span className="text-xs font-medium hidden lg:inline whitespace-nowrap">
                    {step.label}
                  </span>
                </button>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-1 ${
                    index < currentStepIndex ? 'bg-green-400' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">{t('integrations.error', 'Error')}</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {currentStep === 'basic' && (
            <BasicInfoStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'connection' && (
            <ConnectionStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'data_types' && (
            <DataSyncTypesStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'mapping' && (
            <FieldMappingStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'transformation' && (
            <TransformationStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'validation' && (
            <ValidationStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'schedule' && (
            <ScheduleStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'error_handling' && (
            <ErrorHandlingStep formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 'review' && (
            <ReviewStep
              formData={formData}
              updateFormData={updateFormData}
              testResult={testResult}
              testing={testing}
              onTest={handleTestConnection}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0">
          <button
            onClick={handleBack}
            disabled={isFirstStep}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('integrations.back', 'Back')}
          </button>

          <div className="text-sm text-gray-600 dark:text-gray-300">
            Step {currentStepIndex + 1} of {STEPS.length}
          </div>

          {isLastStep ? (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  {t('integrations.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {isEditing ? 'Update Integration' : 'Create Integration'}
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              {t('integrations.next', 'Next')}
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Step 1: Basic Info
interface StepProps {
  formData: Partial<IntegrationConfig>;
  updateFormData: (updates: Partial<IntegrationConfig>) => void;
}

const BasicInfoStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const integrationTypes: { value: IntegrationType; label: string; description: string }[] = [
    {
      value: 'csv_excel',
      label: 'CSV / Excel Upload',
      description: 'Upload CSV/Excel files with AI-powered field mapping'
    },
    {
      value: 'health_plan',
      label: 'Health Plan',
      description: '834 EDI files, member rosters, eligibility data'
    },
    {
      value: 'fhir',
      label: 'FHIR / EMR',
      description: 'Electronic Medical Records, FHIR R4 API'
    },
    {
      value: 'crm',
      label: 'CRM System',
      description: 'Salesforce, HubSpot, tasks and contacts'
    },
    {
      value: 'housing',
      label: 'Housing System',
      description: 'HMIS, assessments, placements, waitlist'
    },
    {
      value: 'benefit_systems',
      label: 'HRIS / Benefit Systems',
      description: 'Workday, ADP, SAP SuccessFactors, UKG - employee & benefits data'
    },
  ];

  const adaptersByType: Record<string, { value: string; label: string }[]> = {
    csv_excel: [
      { value: 'csv_upload', label: 'CSV Upload with AI Mapping' },
      { value: 'excel_upload', label: 'Excel Upload with AI Mapping' },
    ],
    health_plan: [
      { value: 'health_plan_834', label: '834 EDI Parser' },
      { value: 'health_plan_csv', label: 'CSV/Excel Roster' },
    ],
    fhir: [
      { value: 'fhir_r4', label: 'FHIR R4 API' },
    ],
    crm: [
      { value: 'salesforce', label: 'Salesforce' },
      { value: 'hubspot', label: 'HubSpot' },
      { value: 'generic_crm', label: 'Generic REST API' },
    ],
    housing: [
      { value: 'hmis_api', label: 'HMIS API' },
      { value: 'housing_api', label: 'Housing Authority API' },
    ],
    benefit_systems: [
      { value: 'workday_api', label: 'Workday API' },
      { value: 'workday_file', label: 'Workday File Import (CSV/Excel)' },
      { value: 'adp_api', label: 'ADP Workforce Now API' },
      { value: 'adp_file', label: 'ADP File Import (CSV/Excel)' },
      { value: 'sap_successfactors_api', label: 'SAP SuccessFactors API' },
      { value: 'sap_successfactors_file', label: 'SAP SuccessFactors File Import' },
      { value: 'ukg_api', label: 'UKG Pro API' },
      { value: 'ukg_file', label: 'UKG File Import (CSV/Excel)' },
      { value: 'generic_hris', label: 'Generic HRIS API' },
    ],
    api: [
      { value: 'rest_api', label: 'REST API' },
      { value: 'graphql', label: 'GraphQL API' },
    ],
    webhook: [
      { value: 'webhook_inbound', label: 'Inbound Webhook' },
      { value: 'webhook_outbound', label: 'Outbound Webhook' },
    ],
    custom: [
      { value: 'custom_adapter', label: 'Custom Adapter' },
    ],
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.basic_information', 'Basic Information')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('integrations.configure_the_basic_details_for', 'Configure the basic details for this integration')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('integrations.integration_name', 'Integration Name *')}
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => updateFormData({ name: e.target.value })}
          placeholder="e.g., LA County Health Plan Integration"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('integrations.description', 'Description')}
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
          placeholder={t('integrations.describe_the_purpose_of_this', 'Describe the purpose of this integration...')}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('integrations.integration_type', 'Integration Type *')}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {integrationTypes.map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => {
                const adapters = adaptersByType[type.value];
                updateFormData({
                  integration_type: type.value,
                  adapter_type: adapters && adapters.length > 0 ? adapters[0].value : ''
                });
              }}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                formData.integration_type === type.value
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="font-medium text-gray-900 dark:text-white">{type.label}</p>
              <p className="text-xs text-gray-600 mt-1">{type.description}</p>
            </button>
          ))}
        </div>
      </div>

      {formData.integration_type && adaptersByType[formData.integration_type] && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('integrations.adapter_type', 'Adapter Type *')}
          </label>
          <select
            value={formData.adapter_type}
            onChange={(e) => updateFormData({ adapter_type: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            required
          >
            {adaptersByType[formData.integration_type]?.map(adapter => (
              <option key={adapter.value} value={adapter.value}>
                {adapter.label}
              </option>
            )) || []}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('integrations.flow_direction', 'Flow Direction *')}
        </label>
        <div className="grid grid-cols-3 gap-3">
          {[
            { value: 'inbound', label: 'Inbound', description: 'Import data into Trust Marketplace' },
            { value: 'outbound', label: 'Outbound', description: 'Export data to external system' },
            { value: 'bidirectional', label: 'Bidirectional', description: 'Import and export data' }
          ].map(flow => (
            <button
              key={flow.value}
              type="button"
              onClick={() => updateFormData({ flow_direction: flow.value as any })}
              className={`p-3 border-2 rounded-lg text-left transition-all ${
                (formData.flow_direction || 'inbound') === flow.value
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="font-medium text-sm text-gray-900 dark:text-white">{flow.label}</p>
              <p className="text-xs text-gray-600 mt-1">{flow.description}</p>
            </button>
          ))}
        </div>
      </div>

      {formData.integration_type === 'csv_excel' && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-teal-900">{t('integrations.aipowered_field_mapping', 'AI-Powered Field Mapping')}</p>
              <p className="text-sm text-teal-700 mt-1">
                This integration uses Claude AI to automatically suggest field mappings based on your CSV/Excel column headers.
                You can review and adjust the suggestions before importing data.
              </p>
            </div>
          </div>
        </div>
      )}

      {(formData.flow_direction === 'outbound' || formData.flow_direction === 'bidirectional') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">{t('integrations.outbound_data_export', 'Outbound Data Export')}</p>
              <p className="text-sm text-blue-700 mt-1">
                Configure reverse field mappings and export schedules in the subsequent steps to push data
                from Trust Marketplace to external systems.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => updateFormData({ is_active: e.target.checked })}
          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
        />
        <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
          {t('integrations.enable_this_integration_immediately_after', 'Enable this integration immediately after creation')}
        </label>
      </div>
    </div>
  );
};

// Step 2: Connection Configuration
const ConnectionStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { t } = useAppTranslation();
  const connectionMethods = ['sftp', 'ftp', 'http', 'file'];
  const authTypes = ['bearer', 'api_key', 'oauth2', 'username_password', 'ssh_key'];

  const updateConnectionConfig = (updates: Record<string, any>) => {
    updateFormData({
      connection_config: { ...formData.connection_config, ...updates }
    });
  };

  const method = formData.connection_config?.method || 'http';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('integrations.connection_configuration', 'Connection Configuration')}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('integrations.configure_how_to_connect_to', 'Configure how to connect to the external system')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('integrations.connection_method', 'Connection Method *')}
        </label>
        <select
          value={method}
          onChange={(e) => updateConnectionConfig({ method: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          {connectionMethods.map(m => (
            <option key={m} value={m}>{m.toUpperCase()}</option>
          ))}
        </select>
      </div>

      {(method === 'sftp' || method === 'ftp') && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('integrations.host', 'Host *')}
              </label>
              <input
                type="text"
                value={formData.connection_config?.host || ''}
                onChange={(e) => updateConnectionConfig({ host: e.target.value })}
                placeholder="sftp.example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('integrations.port', 'Port')}
              </label>
              <input
                type="number"
                value={formData.connection_config?.port || (method === 'sftp' ? 22 : 21)}
                onChange={(e) => updateConnectionConfig({ port: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('integrations.remote_path', 'Remote Path')}
            </label>
            <input
              type="text"
              value={formData.connection_config?.remote_path || ''}
              onChange={(e) => updateConnectionConfig({ remote_path: e.target.value })}
              placeholder="/outbound/files"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('integrations.file_pattern', 'File Pattern')}
            </label>
            <input
              type="text"
              value={formData.connection_config?.file_pattern || ''}
              onChange={(e) => updateConnectionConfig({ file_pattern: e.target.value })}
              placeholder="*.834 or enrollment_*.csv"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </>
      )}

      {method === 'http' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('integrations.base_url', 'Base URL *')}
            </label>
            <input
              type="url"
              value={formData.connection_config?.base_url || ''}
              onChange={(e) => updateConnectionConfig({ base_url: e.target.value })}
              placeholder="https://api.example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('integrations.authentication_type', 'Authentication Type')}
            </label>
            <select
              value={formData.connection_config?.auth_type || 'bearer'}
              onChange={(e) => updateConnectionConfig({ auth_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {authTypes.map(type => (
                <option key={type} value={type}>
                  {type.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {formData.connection_config?.auth_type === 'oauth2' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('integrations.token_url', 'Token URL')}
              </label>
              <input
                type="url"
                value={formData.connection_config?.token_url || ''}
                onChange={(e) => updateConnectionConfig({ token_url: e.target.value })}
                placeholder="https://auth.example.com/oauth/token"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          )}
        </>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">{t('integrations.credentials', 'Credentials')}</p>
            <p className="text-sm text-blue-700 mt-1">
              Credentials (passwords, API keys, tokens) will be encrypted and stored securely.
              Configure them in the credentials manager after creating the integration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Continued in next file due to length...
